import React, { useEffect, useState } from 'react';
import {Button, DatePicker, Spin, Table, Tabs, Typography, Row, Col, message, Flex} from 'antd';
import Container from '../../components/UI/Container/Container';
import Block from '../../components/UI/Block/Block';
import { API } from '../../http';
import { saveAs } from 'file-saver';
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [activeTab, setActiveTab] = useState('balances');
  const [dateRange, setDateRange] = useState([]);

  const fetchReport = async () => {
    let start, end;
    if (dateRange && dateRange.length === 2) {
      [start, end] = dateRange.map((date) => date.format('YYYY-MM-DD'));
    }

    if ((activeTab === 'sepa' || activeTab === 'fees') && (!start || !end)) {
      message.info('Date range is required for Sepa and Fees reports');
      return;
    }

    setLoading(true);

    let endpoint = '';
    switch (activeTab) {
      case 'balances':
        endpoint = '/admin/balances_report';
        break;
      case 'sepa':
        endpoint = '/admin/sepa_report';
        break;
      case 'fees':
        endpoint = '/admin/fees_report';
        break;
      default:
        return;
    }

    try {
      const requestBody = start && end ? { start, end } : {};
      const { data } = await API.post(endpoint, requestBody);

      const rows = data.split('\n').filter((row) => row);
      const headers = rows[0]?.split(',') || [];
      let values = rows.slice(1).map((row) => row.split(','));

      const dateColumnIndex = headers.indexOf('date'); // Замените 'date' на реальный заголовок столбца с датой

      if (dateColumnIndex !== -1) {
        values = values.sort((a, b) => dayjs(b[dateColumnIndex]).diff(dayjs(a[dateColumnIndex])));
      }

      setColumns(
        headers.map((header, index) => ({
          title: header,
          dataIndex: header,
          key: header,
        }))
      );

      setTableData(
        values.map((valueArray, index) => {
          const rowObj = {};
          headers.forEach((header, idx) => {
            rowObj[header] = valueArray[idx];
          });
          return { key: index, ...rowObj };
        })
      );
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (!tableData.length) return;
    let start, end;
    if (dateRange && dateRange.length === 2) {
      [start, end] = dateRange.map((date) => date.format('YYYY-MM-DD'));
    }

    const csvHeader = columns.map((col) => col.title).join(',');
    const csvRows = tableData.map((row) => columns.map((col) => row[col.dataIndex]).join(','));
    const csvContent = [csvHeader, ...csvRows].join('\n');

    const filename = start && end && activeTab !== 'balances' ? `report_${start}_to_${end}.csv` : 'report.csv';

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, filename);
  };

  const onTabChange = (key) => {
    setTableData([]);
    setColumns([]);
    setActiveTab(key);
  };

  const onDateChange = (dates) => {
    if (dates) {
      setDateRange(dates);
    } else {
      setDateRange([]);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, activeTab]);

  return (
    <Container>
      <Spin tip="Loading..." spinning={loading}>
        <Block>
          <Typography.Title level={2}>Reports</Typography.Title>
          <Flex wrap="wrap" gap={10} justify="space-between" align="middle" style={{ marginBottom: 20, width: '100%' }}>
            <RangePicker
              style={{ opacity: activeTab === 'balances' ? 0 : 1 }}
              value={dateRange}
              onChange={onDateChange}
              variant="filled"
              size="large"
            />
            <Button
              type="primary"
              size="large"
              onClick={downloadCSV}
              disabled={!tableData.length}
            >
              Download CSV
            </Button>
          </Flex>
          <Tabs activeKey={activeTab} onChange={onTabChange}>
            <Tabs.TabPane tab="Balances" key="balances" />
            <Tabs.TabPane tab="Sepa" key="sepa" />
            <Tabs.TabPane tab="Fees" key="fees" />
          </Tabs>
          <Table
            style={{ marginBottom: 20 }}
            pagination={{ position: ['none', 'bottomCenter'], pageSize: 20, hideOnSinglePage: true }}
            columns={columns}
            dataSource={tableData}
            scroll={{ x: 'max-content' }}
          />
        </Block>
      </Spin>
    </Container>
  );
};

export default Reports;
